<script setup>
import { ref, computed, watch } from 'vue'
import { getLogTypeColor } from '@/constants/logTypes'
import { useGroupMode } from '@/composables/useGroupMode'

// Compact count formatter: 4-5 chars max (e.g. 903, 1.1k, 20.3k, 100k, 103m, 1.2b)
function formatCount(n) {
  if (n < 1000) return n.toString()
  const units = [
    { v: 1e9, s: 'b' },
    { v: 1e6, s: 'm' },
    { v: 1e3, s: 'k' },
  ]
  for (const u of units) {
    if (n >= u.v) {
      const value = n / u.v
      return (value >= 100 ? Math.floor(value).toString() : value.toFixed(1)) + u.s
    }
  }
  return n.toString()
}

const props = defineProps({
  logTypeDistribution: {
    type: Array,
    default: () => []
  },
  sourceLogTypeDistribution: {
    type: Array,
    default: () => []
  },
  activeLogType: {
    type: String,
    default: null
  },
  activeSource: {
    type: String,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select', 'select-source', 'select-source-log-type'])

// Grouping mode (shared across views, persisted to localStorage)
const { groupMode } = useGroupMode()

// Track which sources are expanded (source mode)
const expandedSources = ref({})

// Select a log type
function selectLogType(logType) {
  emit('select', logType)
}

// Select a source (toggle)
function selectSource(sourceName) {
  emit('select-source', sourceName)
}

// Select a log type from within an expanded source — applies both atomically
function selectFromSource(source, logType) {
  emit('select-source-log-type', { source, logType })
}

// Toggle source expansion (source mode)
function toggleExpandedSource(sourceName) {
  const wasExpanded = expandedSources.value[sourceName]
  // Collapse any other expanded source
  Object.keys(expandedSources.value).forEach(s => {
    if (s !== sourceName && expandedSources.value[s]) {
      expandedSources.value[s] = false
    }
  })
  expandedSources.value[sourceName] = !wasExpanded
}

// Check if log type is active
function isActive(logType) {
  return props.activeLogType === logType
}

// --- Scroll containment (source mode): split items into top / expanded / bottom ---

// Source mode: which source is expanded
const expandedSourceKey = computed(() => {
  for (const [s, expanded] of Object.entries(expandedSources.value)) {
    if (expanded) return s
  }
  return null
})

const sourceTopItems = computed(() => {
  if (!expandedSourceKey.value) return props.sourceLogTypeDistribution
  const idx = props.sourceLogTypeDistribution.findIndex(d => d.source === expandedSourceKey.value)
  if (idx === -1) return props.sourceLogTypeDistribution
  return props.sourceLogTypeDistribution.slice(0, idx)
})

const sourceExpandedItem = computed(() => {
  if (!expandedSourceKey.value) return null
  return props.sourceLogTypeDistribution.find(d => d.source === expandedSourceKey.value) || null
})

const sourceBottomItems = computed(() => {
  if (!expandedSourceKey.value) return []
  const idx = props.sourceLogTypeDistribution.findIndex(d => d.source === expandedSourceKey.value)
  if (idx === -1) return []
  return props.sourceLogTypeDistribution.slice(idx + 1)
})

// Collapse all sources except the given one
function collapseAllSourcesExcept(keepSource) {
  Object.keys(expandedSources.value).forEach(s => {
    if (s !== keepSource) expandedSources.value[s] = false
  })
}

// Find which source contains a given log type
function findSourceForLogType(logType) {
  if (!logType) return null
  const entry = props.sourceLogTypeDistribution.find(
    s => s.log_types.some(lt => lt.log_type === logType)
  )
  return entry?.source || null
}

// In source mode: auto-expand the active source (or the source containing the active log type)
watch(() => props.activeSource, (source) => {
  if (groupMode.value !== 'source') return
  if (source) {
    collapseAllSourcesExcept(source)
    expandedSources.value[source] = true
  } else {
    // Source deselected — expand source containing current log type
    const ownerSource = findSourceForLogType(props.activeLogType)
    collapseAllSourcesExcept(ownerSource)
    if (ownerSource) expandedSources.value[ownerSource] = true
  }
})

// Reset expansions when switching modes, then auto-expand if applicable
watch(groupMode, (mode) => {
  Object.keys(expandedSources.value).forEach(s => {
    expandedSources.value[s] = false
  })

  // Auto-expand in source mode
  if (mode === 'source') {
    const source = props.activeSource || findSourceForLogType(props.activeLogType)
    if (source) expandedSources.value[source] = true
  }
})
</script>

<template>
  <div class="log-types-sidebar">
    <div class="sidebar-header">
      <span class="header-title">{{ groupMode === 'source' ? 'Sources' : 'Log Types' }}</span>
    </div>

    <div class="sidebar-content">
      <!-- Empty state -->
      <div v-if="logTypeDistribution.length === 0 && !loading" class="empty-state">
        <span>No log types</span>
      </div>

      <!-- Loading state -->
      <div v-if="loading && logTypeDistribution.length === 0" class="loading-state">
        <div class="spinner-small"></div>
      </div>

      <!-- ============ LOG TYPE MODE ============ -->
      <template v-if="groupMode === 'logtype' && logTypeDistribution.length > 0">
        <div class="scroll-zone">
          <div class="log-type-list">
            <div
              v-for="dist in logTypeDistribution"
              :key="dist.log_type"
              class="log-type-item"
            >
              <div
                class="log-type-row"
                :class="{ 'active': isActive(dist.log_type) }"
                @click="selectLogType(dist.log_type)"
              >
                <div class="log-type-info">
                  <span class="log-type-color" :style="{ backgroundColor: getLogTypeColor(dist.log_type) }"></span>
                  <span class="log-type-name">{{ dist.log_type }}</span>
                  <span class="log-type-count">{{ formatCount(dist.count) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ============ SOURCE MODE ============ -->
      <template v-if="groupMode === 'source' && sourceLogTypeDistribution.length > 0">
        <!-- No expansion: single scrollable list -->
        <div v-if="!sourceExpandedItem" class="scroll-zone">
          <div class="log-type-list">
            <div
              v-for="src in sourceLogTypeDistribution"
              :key="src.source"
              class="log-type-item"
            >
              <div
                class="source-row"
                :class="{ 'active': activeSource === src.source }"
                @click="selectSource(src.source)"
              >
                <div class="log-type-info">
                  <font-awesome-icon :icon="['fas', 'server']" class="source-icon" :style="{ color: getLogTypeColor(src.source) }" />
                  <span class="log-type-name">{{ src.source }}</span>
                  <span class="log-type-count">{{ formatCount(src.count) }}</span>
                </div>
                <button
                  v-if="src.log_types.length > 0"
                  class="expand-btn"
                  @click.stop="toggleExpandedSource(src.source)"
                  title="Show log types"
                >
                  <font-awesome-icon :icon="['fas', 'chevron-down']" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- With expansion: three-zone layout -->
        <template v-else>
          <!-- Top sources (fixed) -->
          <div v-if="sourceTopItems.length > 0" class="zone-fixed">
            <div class="log-type-list">
              <div v-for="src in sourceTopItems" :key="src.source" class="log-type-item">
                <div
                  class="source-row"
                  :class="{ 'active': activeSource === src.source }"
                  @click="selectSource(src.source)"
                >
                  <div class="log-type-info">
                    <font-awesome-icon :icon="['fas', 'server']" class="source-icon" :style="{ color: getLogTypeColor(src.source) }" />
                    <span class="log-type-name">{{ src.source }}</span>
                    <span class="log-type-count">{{ formatCount(src.count) }}</span>
                  </div>
                  <button
                    v-if="src.log_types.length > 0"
                    class="expand-btn"
                    @click.stop="toggleExpandedSource(src.source)"
                    title="Show log types"
                  >
                    <font-awesome-icon :icon="['fas', 'chevron-down']" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Expanded source header (fixed) -->
          <div class="zone-fixed">
            <div class="log-type-item">
              <div
                class="source-row"
                :class="{ 'active': activeSource === sourceExpandedItem.source }"
                @click="selectSource(sourceExpandedItem.source)"
              >
                <div class="log-type-info">
                  <font-awesome-icon :icon="['fas', 'server']" class="source-icon" :style="{ color: getLogTypeColor(sourceExpandedItem.source) }" />
                  <span class="log-type-name">{{ sourceExpandedItem.source }}</span>
                  <span class="log-type-count">{{ formatCount(sourceExpandedItem.count) }}</span>
                </div>
                <button
                  class="expand-btn"
                  @click.stop="toggleExpandedSource(sourceExpandedItem.source)"
                  title="Hide log types"
                >
                  <font-awesome-icon :icon="['fas', 'chevron-up']" />
                </button>
              </div>
            </div>
          </div>

          <!-- Nested log types (scrollable middle) -->
          <div class="zone-scroll">
            <div class="nested-log-types">
              <div
                v-for="lt in sourceExpandedItem.log_types"
                :key="lt.log_type"
                class="nested-log-type-row"
                :class="{ 'active': activeSource === sourceExpandedItem.source && isActive(lt.log_type) }"
                @click="selectFromSource(sourceExpandedItem.source, lt.log_type)"
              >
                <span class="log-type-color" :style="{ backgroundColor: getLogTypeColor(lt.log_type) }"></span>
                <span class="log-type-name">{{ lt.log_type }}</span>
                <span class="log-type-count">{{ formatCount(lt.count) }}</span>
              </div>
            </div>
          </div>

          <!-- Bottom sources (fixed) -->
          <div v-if="sourceBottomItems.length > 0" class="zone-fixed">
            <div class="log-type-list">
              <div v-for="src in sourceBottomItems" :key="src.source" class="log-type-item">
                <div
                  class="source-row"
                  :class="{ 'active': activeSource === src.source }"
                  @click="selectSource(src.source)"
                >
                  <div class="log-type-info">
                    <font-awesome-icon :icon="['fas', 'server']" class="source-icon" :style="{ color: getLogTypeColor(src.source) }" />
                    <span class="log-type-name">{{ src.source }}</span>
                    <span class="log-type-count">{{ formatCount(src.count) }}</span>
                  </div>
                  <button
                    v-if="src.log_types.length > 0"
                    class="expand-btn"
                    @click.stop="toggleExpandedSource(src.source)"
                    title="Show log types"
                  >
                    <font-awesome-icon :icon="['fas', 'chevron-down']" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <!-- Mode toggle footer -->
    <div class="sidebar-footer">
      <button
        class="mode-btn"
        :class="{ active: groupMode === 'logtype' }"
        @click="groupMode = 'logtype'"
        title="Group by log type"
      >
        <font-awesome-icon :icon="['fas', 'list']" />
      </button>
      <button
        class="mode-btn"
        :class="{ active: groupMode === 'source' }"
        @click="groupMode = 'source'"
        title="Group by source"
      >
        <font-awesome-icon :icon="['fas', 'server']" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.log-types-sidebar {
  width: 200px;
  min-width: 200px;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.header-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}

.sidebar-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0.5rem;
  gap: 0.25rem;
}

/* Single scrollable zone (no expansion) */
.scroll-zone {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Three-zone layout for scroll containment (source mode) */
.zone-fixed {
  flex-shrink: 0;
  overflow: hidden;
}

.zone-scroll {
  flex: 0 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* Custom scrollbar */
.scroll-zone::-webkit-scrollbar,
.zone-scroll::-webkit-scrollbar {
  width: 6px;
}

.scroll-zone::-webkit-scrollbar-track,
.zone-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.scroll-zone::-webkit-scrollbar-thumb,
.zone-scroll::-webkit-scrollbar-thumb {
  background: var(--border-strong);
  border-radius: 10px;
}

.scroll-zone::-webkit-scrollbar-thumb:hover,
.zone-scroll::-webkit-scrollbar-thumb:hover {
  background: var(--surface-control);
}

.empty-state,
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: var(--text-faint);
  font-size: 0.875rem;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid var(--text-disabled);
  border-top: 2px solid var(--accent);
  border-radius: var(--radius-pill);
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.log-type-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.log-type-item {
  display: flex;
  flex-direction: column;
}

.log-type-row,
.source-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
  background: transparent;
  border: 1px solid transparent;
}

.log-type-row:hover,
.source-row:hover {
  background: var(--surface-overlay-2);
}

.log-type-row.active,
.source-row.active {
  background: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.4);
}

.log-type-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.log-type-color {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-xs);
  flex-shrink: 0;
}

.source-icon {
  font-size: 0.65rem;
  flex-shrink: 0;
  width: 8px;
}

.log-type-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.log-type-row.active .log-type-name,
.source-row.active .log-type-name {
  color: var(--text-heading);
}

.log-type-count {
  font-size: 0.75rem;
  color: var(--text-faint);
  flex-shrink: 0;
  margin-left: auto;
}

.log-type-row.active .log-type-count,
.source-row.active .log-type-count {
  color: var(--text-muted);
}

.expand-btn {
  background: transparent;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  font-size: 0.7rem;
}

.expand-btn:hover {
  color: var(--text-primary);
}

/* Nested log types under a source */
.nested-log-types {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0 0.25rem 0.5rem;
  gap: 0.125rem;
}

.nested-log-type-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);
  border: 1px solid transparent;
}

.nested-log-type-row:hover {
  background: var(--surface-overlay-2);
}

.nested-log-type-row.active {
  background: rgba(115, 87, 210, 0.2);
  border-color: rgba(115, 87, 210, 0.4);
}

.nested-log-type-row .log-type-name {
  font-size: 0.8rem;
  font-weight: 500;
}

.nested-log-type-row .log-type-count {
  margin-left: auto;
}

/* Footer toggle */
.sidebar-footer {
  display: flex;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.mode-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem;
  background: transparent;
  border: none;
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.75rem;
  transition: var(--transition);
}

.mode-btn:hover {
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.03);
}

.mode-btn.active {
  color: var(--accent);
  background: var(--accent-soft-2);
}
</style>
